import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Menu from "./Menu";
import { logout } from "../../../common/actionCreators/";
import {
  getPushStatus,
  enablePush,
  disablePush
} from "../../utils/pushUtil";
import {
  isStandalone,
  isIos,
  canPromptInstall,
  promptInstall,
  subscribeInstallState
} from "../../utils/installPrompt";
import {
  isCartEnabled,
  isCartFeatureKnown,
  setCartEnabled,
  subscribeCartFeature,
  applyCartBetaValue
} from "../../utils/cartFeature";
import baseApi from "../../api/baseApi";

const MenuContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // redux state 변경(로그인/로그아웃) 시 Menu 가 즉시 re-render 되도록 구독.
  // redux 에 loginInfo 가 없어도 새로고침 등으로 localStorage 토큰만 있는 경우 살림.
  const loginInfo = useSelector(({ loginReducer }) => loginReducer.loginInfo);
  const isLoggedIn =
    !!(loginInfo && loginInfo.token) ||
    !!(typeof window !== "undefined" && window.localStorage.getItem("token"));
  const isAdmin =
    (loginInfo && loginInfo.adminYn === "Y") ||
    (typeof window !== "undefined" &&
      window.localStorage.getItem("adminYn") === "Y");

  // 푸시 알림 상태(로그인 상태에서만 의미 있음). supported=false 면 UI 숨김.
  const [pushStatus, setPushStatus] = useState({ supported: false, permission: "default", subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);

  // 보관함 베타 활성 여부(계정별, 서버 저장). known=false 면 아직 조회 전 → 메뉴 감춰 깜빡임 방지.
  const [cartEnabled, setCartEnabledState] = useState(isCartEnabled());
  const [cartKnown, setCartKnown] = useState(isCartFeatureKnown());
  const [cartBusy, setCartBusy] = useState(false);

  // 설치 prompt 상태. 변경되면 강제 re-render 위해 increment.
  const [installTick, setInstallTick] = useState(0);
  const [iosHintOpen, setIosHintOpen] = useState(false);
  const standalone = isStandalone();
  const installable = canPromptInstall();
  const ios = isIos();

  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    getPushStatus().then(s => { if (!cancelled) setPushStatus(s); });
    return () => { cancelled = true; };
  }, [isLoggedIn]);

  // beforeinstallprompt / appinstalled 이벤트로 설치 가능 여부 바뀌면 re-render.
  useEffect(() => {
    const unsub = subscribeInstallState(() => setInstallTick(t => t + 1));
    return unsub;
  }, []);

  // 보관함 베타 토글 변경 구독 — 값/조회여부 함께 반영.
  useEffect(
    () =>
      subscribeCartFeature(enabled => {
        setCartEnabledState(enabled);
        setCartKnown(isCartFeatureKnown());
      }),
    []
  );

  // 로그인 상태면 서버(myInfo)에서 보관함 베타 값 hydrate.
  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    baseApi
      .post("/member/myInfo")
      .then(res => {
        if (cancelled) return;
        const yn =
          res && res.data && res.data.myInfo && res.data.myInfo.cartBetaYn;
        applyCartBetaValue(yn);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  // 보관함 베타 on/off 토글 (우측 아이콘 버튼). 서버 저장 성공 후 반영.
  const onToggleCart = useCallback(() => {
    if (cartBusy) return;
    setCartBusy(true);
    setCartEnabled(!cartEnabled)
      // eslint-disable-next-line no-alert
      .catch(e => alert(e && e.message ? e.message : "설정 변경에 실패했습니다."))
      .then(() => setCartBusy(false));
  }, [cartBusy, cartEnabled]);

  const onInstallClick = useCallback(async () => {
    if (canPromptInstall()) {
      await promptInstall();
      return;
    }
    if (ios) {
      setIosHintOpen(true);
      return;
    }
    // 비지원 브라우저: 안내
    // eslint-disable-next-line no-alert
    alert("이 브라우저는 설치 prompt 를 지원하지 않습니다.\n브라우저 메뉴에서 '홈 화면에 추가' 항목을 찾아 주세요.");
  }, [ios]);

  const onCloseIosHint = useCallback(() => setIosHintOpen(false), []);

  const onTogglePush = useCallback(async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushStatus.subscribed) {
        await disablePush();
      } else {
        await enablePush();
      }
      const next = await getPushStatus();
      setPushStatus(next);
    } catch (e) {
      // 권한 거부·서버 오류 등. alert 한 번만.
      // eslint-disable-next-line no-alert
      alert(e && e.message ? e.message : "알림 설정 변경 실패");
    } finally {
      setPushBusy(false);
    }
  }, [pushBusy, pushStatus.subscribed]);

  const onButtonClick = useCallback(() => {
    // dispatch 로 redux state 비움 + localStorage 정리. (rootReducer LOGOUT 처리)
    dispatch(logout());
    // 모바일 / SW 가 cache 한 state·번들이 남아있는 케이스 회피 — 강제 새로고침으로
    // 앱 전체를 fresh 마운트. 한 번뿐인 액션이라 SPA smooth 비용 무시 가능.
    window.location.href = "/";
  }, [dispatch]);

  const onButtonClickForModifyMember = useCallback(() => {
    navigate("/updateUserInfo");
  }, [navigate]);

  // 설치 버튼 노출 — standalone(이미 설치) 면 숨김.
  // Chrome 계열 installable=true 면 누르면 prompt. iOS 면 누르면 안내 모달.
  const showInstall = !standalone && (installable || ios);
  // installTick 은 useEffect 트리거 후 re-render 보장용 (위에서 가져온 installable 갱신).
  void installTick;

  return (
    <Menu
      isLoggedIn={isLoggedIn}
      isAdmin={isAdmin}
      cartEnabled={cartEnabled}
      cartKnown={cartKnown}
      cartBusy={cartBusy}
      onToggleCart={onToggleCart}
      onButtonClick={onButtonClick}
      onButtonClickForModifyMember={onButtonClickForModifyMember}
      pushSupported={pushStatus.supported}
      pushSubscribed={pushStatus.subscribed}
      pushPermission={pushStatus.permission}
      pushBusy={pushBusy}
      onTogglePush={onTogglePush}
      showInstall={showInstall}
      onInstallClick={onInstallClick}
      iosHintOpen={iosHintOpen}
      onCloseIosHint={onCloseIosHint}
    />
  );
};

export default MenuContainer;
